import { useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { BookmarkPlus } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { IssueTable, TableSkeleton } from '@/components/tables/issue-table'
import { ViewHeader } from '@/components/shell/view-header'
import { SaveSearchDialog } from '@/components/saved-search/save-search-dialog'
import { FacetBar } from '@/components/saved-search/facet-bar'
import { SortMenu } from '@/components/common/sort-menu'
import { fieldByName, useIssueFields, useIssues } from '@/lib/query'
import { ViewError } from '@/components/common/query-states'
import { filterIssues, parseFilter } from '@/lib/filters'
import { sortIssues } from '@/lib/sort'
import { currentSprint } from '@/lib/sprint'
import { useMe } from '@/hooks/use-me'
import { useSavedSearches } from '@/hooks/use-saved-searches'
import { useSortPreference } from '@/hooks/use-sort-preference'

/** Saved searches: /q?filter=<GitHub-search-shaped string>, evaluated locally. */
export default function SavedSearch() {
  const [searchParams] = useSearchParams()
  const filterText = searchParams.get('filter') ?? ''
  const nameParam = searchParams.get('name')
  const { data: issues, isPending, isError, error, refetch } = useIssues()
  const fields = useIssueFields().data
  const me = useMe()
  const saved = useSavedSearches().data
  const [sort] = useSortPreference()
  const [saveOpen, setSaveOpen] = useState(false)

  // If this filter is already saved, show its name instead of "Save".
  const existing = useMemo(() => {
    const all = [...(saved?.org ?? []), ...(saved?.mine ?? [])]
    return all.find((s) => s.filter === filterText)
  }, [saved, filterText])

  // The human label: a stored search's name wins; otherwise the label the
  // sidebar passed through (?name=); otherwise just the raw filter.
  const displayName = existing?.name ?? nameParam ?? null

  const items = useMemo(() => {
    const sprintOptions = fieldByName(fields, 'Sprint')?.options.map((o) => o.name) ?? []
    const ctx = {
      currentSprintName: currentSprint(sprintOptions)?.name ?? null,
      viewerLogin: me?.login,
    }
    return sortIssues(filterIssues(issues ?? [], parseFilter(filterText), ctx), sort)
  }, [issues, fields, filterText, me, sort])

  return (
    <div className="flex h-full flex-col">
      <ViewHeader
        title={displayName ? `Saved search: ${displayName}` : filterText ? 'Saved search' : 'New search'}
        meta={
          <span className="font-mono text-[11px]" title={filterText}>
            {displayName
              ? `${items.length} items`
              : filterText
                ? `${filterText} · ${items.length} items`
                : 'Pick filters below to build a search'}
          </span>
        }
        chips={null}
        toolbar={
          <div className="flex items-center gap-1">
            <SortMenu />
            {filterText ? (
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-[11px]"
                onClick={() => setSaveOpen(true)}
              >
                <BookmarkPlus className="size-3.5" />
                {existing ? 'Save as…' : 'Save search'}
              </Button>
            ) : null}
          </div>
        }
      />
      <FacetBar filter={filterText} />
      {isError ? (
        <ViewError what="issues" error={error} onRetry={() => refetch()} />
      ) : isPending ? (
        <TableSkeleton />
      ) : (
        <IssueTable
          issues={items}
          bulkKind="work"
          searchable={false}
          emptyTitle={filterText ? 'No matching issues' : 'Build a search'}
          emptyHint={
            filterText ? 'Try loosening the filters above.' : 'Pick filters above to get started.'
          }
        />
      )}
      {/* Mounted only while open so each open re-seeds from defaultName. */}
      {saveOpen && (
        <SaveSearchDialog
          open
          onOpenChange={setSaveOpen}
          filter={filterText}
          defaultName={existing?.name ?? ''}
        />
      )}
    </div>
  )
}
